import {
  TableHead,
  TableCell,
  Box,
  TableFooter,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material';
import { Row } from 'components/Layout';

export const StyledTable = styled('table', {
  shouldForwardProp: (prop) =>
    prop !== 'hasBorderTopLeftRadius' &&
    prop !== 'hasBorderTopRightRadius' &&
    prop !== 'hasBorderBottomLeftRadius' &&
    prop !== 'hasBorderBottomRightRadius' &&
    prop !== 'isTotalsTable',
})<{
  hasBorderTopLeftRadius?: boolean;
  hasBorderTopRightRadius?: boolean;
  hasBorderBottomLeftRadius?: boolean;
  hasBorderBottomRightRadius?: boolean;
  isTotalsTable?: boolean;
}>(
  ({
    theme,
    hasBorderTopLeftRadius = false,
    hasBorderTopRightRadius = false,
    hasBorderBottomLeftRadius = false,
    hasBorderBottomRightRadius = false,
    isTotalsTable = false,
    hasMinWidth = true,
  }) => ({
    display: 'table',
    tableLayout: 'auto',
    maxWidth: isTotalsTable ? '20%' : undefined,
    width: '100%',
    minWidth: hasMinWidth ? (isTotalsTable ? '20%' : '93%') : undefined,
    backgroundColor: theme.palette.background.paper,
    textAlign: 'left',
    borderCollapse: 'separate',
    borderSpacing: 0,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    borderTopLeftRadius: hasBorderTopLeftRadius
      ? theme.shape.borderRadius
      : '0',
    borderTopRightRadius: hasBorderTopRightRadius
      ? theme.shape.borderRadius
      : '0',
    borderBottomLeftRadius: hasBorderBottomLeftRadius
      ? theme.shape.borderRadius
      : '0',
    borderBottomRightRadius: hasBorderBottomRightRadius
      ? theme.shape.borderRadius
      : '0',
  }),
);

export const StyledRow = styled(TableRow)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& td': {
    fontSize: theme.fontSizes?.sm,
    color: theme.palette.text.darkBodyText,
    padding: theme.spacing(2, 4),
  },
}));

export const InvestorCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'isHeader',
})<{ isHeader?: boolean }>(({ theme, isHeader }) => ({
  borderRight: `1px solid ${theme.palette.divider}`,
  borderBottom: isHeader
    ? `2px solid ${theme.palette.divider}`
    : `1px solid ${theme.palette.divider}`,
}));

export const StyledHeader = styled(TableHead, {
  shouldForwardProp: (prop) => prop !== 'isHeader' && prop !== 'color',
})<{ isHeader?: boolean; color?: 'dark' | 'paper' }>(
  ({ theme, isHeader, color = 'dark' }) => ({
    borderBottom: isHeader
      ? `2px solid ${theme.palette.divider}`
      : `1px solid ${theme.palette.divider}`,
    backgroundColor:
      color === 'dark'
        ? theme.palette.background.header
        : theme.palette.background.paper,
    '& th': {
      padding: theme.spacing(2, 4),
      color: theme.palette.text.darkBodyText,
      fontWeight: theme.typography.fontWeightBold,
    },
  }),
);

export const HeaderCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'isHeader' &&
    prop !== 'textSize' &&
    prop !== 'paddingSize' &&
    prop !== 'backgroundColor',
})<{
  isHeader?: boolean;
  textSize?: 'sm' | 'regular';
  paddingSize?: 'sm' | 'md';
  backgroundColor?: 'paper' | 'dark';
}>(({ theme, isHeader, textSize, paddingSize, backgroundColor }) => ({
  borderBottom: isHeader
    ? `2px solid ${theme.palette.divider}`
    : `1px solid ${theme.palette.divider}`,
  fontSize:
    textSize === 'regular' ? theme.fontSizes?.regular : theme.fontSizes?.sm,
  padding: paddingSize === 'md' ? theme.spacing(3, 4) : theme.spacing(2, 4),
  backgroundColor: backgroundColor
    ? backgroundColor === 'paper'
      ? theme.palette.background.paper
      : theme.palette.background.header
    : undefined,
}));

export const FooterCell = styled(TableCell, {
  shouldForwardProp: (prop) =>
    prop !== 'hasBorderRight' && prop !== 'paddingSize',
})<{ hasBorderRight?: boolean; paddingSize?: 'sm' | 'md' }>(
  ({ theme, hasBorderRight, paddingSize }) => ({
    borderTop: `2px solid ${theme.palette.divider}`,
    borderRight: hasBorderRight ? `1px solid ${theme.palette.divider}` : 'none',
    padding: paddingSize === 'sm' ? theme.spacing(2, 4) : theme.spacing(3, 4),
    whiteSpace: 'nowrap',
  }),
);

export const FooterContainer = styled(TableFooter)(({ theme }) => ({
  '& td': {
    fontSize: theme.fontSizes?.sm,
    color: theme.palette.text.darkBodyText,
    fontWeight: theme.typography.fontWeightBold,
  },
}));

export const ScrollableRow = styled(Row)({
  flexWrap: 'nowrap',
  flexDirection: 'row',
  flex: 1,
  width: '100%',
  marginBottom: '16px',
});

export const ScrollableContainer = styled(Box)({
  overflowX: 'auto',
  width: '100%',
  minWidth: '0',
  maxWidth: '1200px',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: '4px',
  },
});
